import { useState, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import Select from 'react-select'
import * as Yup from 'yup'
import { Formik, Form } from 'formik'
import DatePicker from 'react-datepicker'
import { useTranslation } from 'react-i18next'

import { useIsIglooWorksBuildingConnected } from '@/api/property-keyless/igloo-works/use-is-igloo-works-building-connected'
import { Alert } from '@/components/ui/alert'
import { useSnackbar } from '@/helpers/hooks/useSnackbar'
import { selectUserRole } from '@/reducers/userDataReducer'
import { canEditKeylessAccess } from '@/pages/clients/client-profile/keyless-access/keyless-access.constants'
import { useGetIglooPin } from '@/api/keyless/igloo/get-igloo-pin'
import { useGetIglooCustomPin } from '@/api/keyless/igloo/get-igloo-custom-pin'
import { useIssueIglooPin } from '@/api/keyless/igloo/issue-igloo-pin'
import { useRevokeIglooPin } from '@/api/keyless/igloo/revoke-igloo-pin'
import { useRevokeIglooCustomPin } from '@/api/keyless/igloo/revoke-igloo-custom-pin'
import { useResendIglooPinEmail } from '@/api/keyless/igloo/resend-igloo-pin-email'
import { useResendIglooCustomPinEmail } from '@/api/keyless/igloo/resend-igloo-custom-pin-email'
import { getEarliestKeyExpiry } from '@/api/keyless/igloo/normalize-igloo-devices'
import { EMPTY_IGLOO_PIN } from '@/api/keyless/igloo/normalize-igloo-pin'
import { EMPTY_IGLOO_CUSTOM_PIN } from '@/api/keyless/igloo/normalize-igloo-custom-pin.utils'

import API from '../../../../api/api'
import ConfirmPopup from '../../../Popups/ConfirmPopup'
import { saltoSpaceDropdownStyles } from '../helpers'
import Loader from '../../../../components/Loader/Loader'

import IglooPinBlock from './IglooPinBlock'
import IglooCustomPinBlock from './IglooCustomPinBlock'

const getCurrentDate = () => new Date()
const isDateValid = (date) => date && new Date(date) > getCurrentDate()

// Note: the backend already returns a specific failure reason (e.g. the 29-day algoPIN minimum);
// surface it instead of a generic retry message so the user knows why the action failed.
const getBackendErrorMessage = (error, fallbackMessage) =>
  error?.response?.data?.message || fallbackMessage

const IglooWorkSchema = Yup.object().shape({
  selectedDevices: Yup.array().nullable(),
  expirationDate: Yup.number(),
})

const IglooWorkSection = ({ isAvailable, buildingId, clientId, clientData }) => {
  const { t } = useTranslation(['clients-legacy', 'clients', 'common'])
  const { displaySnackbar } = useSnackbar()
  const { isBuildingConnected, isLoading: isLoadingBuildingConnection } =
    useIsIglooWorksBuildingConnected(buildingId)

  const userRole = useSelector(selectUserRole)
  const canEdit = canEditKeylessAccess(userRole)
  // Note: iglooWorkAlgoPinAvailable is a sibling building flag to iglooWorkAvailable on the
  // same /client/:id payload (see IntegrationsSection.jsx) — no extra fetch needed.
  const algoPinAvailable = Boolean(clientData?.buildingId?.iglooWorkAlgoPinAvailable)
  const customPinAvailable = Boolean(clientData?.buildingId?.iglooWorkCustomPinAvailable)
  const residentName = `${clientData?.user?.firstName ?? ''} ${
    clientData?.user?.lastName ?? ''
  }`.trim()

  const { data: pin, isLoading: pinLoading } = useGetIglooPin({ clientId }, algoPinAvailable)
  const pinData = pin ?? EMPTY_IGLOO_PIN
  const { mutateAsync: issuePin, isPending: isIssuing } = useIssueIglooPin(clientId)
  const { mutateAsync: revokePin, isPending: isRevoking } = useRevokeIglooPin(clientId)
  const { mutateAsync: resendEmail, isPending: isResending } = useResendIglooPinEmail(clientId)

  const { data: customPin, isLoading: customPinLoading } = useGetIglooCustomPin(
    { clientId },
    customPinAvailable,
  )
  const customPinData = customPin ?? EMPTY_IGLOO_CUSTOM_PIN
  const { mutateAsync: revokeCustomPin, isPending: isRevokingCustomPin } =
    useRevokeIglooCustomPin(clientId)
  const { mutate: resendCustomPinEmail, isPending: isResendingCustomPin } =
    useResendIglooCustomPinEmail(clientId)

  const handleIssuePin = async () => {
    try {
      await issuePin()
      displaySnackbar(
        'PIN code access issued successfully. The PIN was sent to the resident by email.',
      )
    } catch (error) {
      displaySnackbar(
        getBackendErrorMessage(error, 'Could not issue PIN code access. Please try again later.'),
        true,
      )
    }
  }

  const handleRevokePin = async () => {
    try {
      await revokePin()
      displaySnackbar('Unit PIN code access has been revoked')
    } catch (error) {
      displaySnackbar(
        getBackendErrorMessage(
          error,
          'Error occurred revoking Unit PIN code access. Please try again',
        ),
        true,
      )
    }
  }

  const handleResendPinEmail = async () => {
    try {
      await resendEmail()
      displaySnackbar('PIN email has been resent')
    } catch (error) {
      displaySnackbar(
        getBackendErrorMessage(error, 'Error occurred resending PIN email. Please try again'),
        true,
      )
    }
  }

  const handleRevokeCustomPin = () => revokeCustomPin().catch(() => undefined)

  const expirationDateInitialValue = isDateValid(clientData?.leaseEnd)
    ? clientData.leaseEnd
    : isDateValid(clientData?.moveOutDate)
      ? clientData.moveOutDate
      : undefined

  const [isLoading, setIsLoading] = useState(true)
  const [isCreatingUser, setIsCreatingUser] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isCreateUserPopupVisible, setIsCreateUserPopupVisible] = useState(false)
  const [isConfirmPopupVisible, setIsConfirmPopupVisible] = useState(false)

  const [isUserConnected, setIsUserConnected] = useState(false)
  const [availableDevices, setAvailableDevices] = useState([])

  const [initialValues, setInitialValues] = useState({
    selectedDevices: [],
    expirationDate: expirationDateInitialValue,
  })

  const formikInitialValues = useMemo(
    () => ({
      selectedDevices: initialValues.selectedDevices || [],
      expirationDate: initialValues.expirationDate,
      confirmed: false,
    }),
    [initialValues.selectedDevices, initialValues.expirationDate],
  )

  const fetchBuildingDevices = async () => {
    try {
      const { data } = await API.getIglooWorkBuildingDevices(buildingId)
      setAvailableDevices(data)
    } catch {
      // not connected or no devices assigned
    }
  }

  const checkConnectionAndFetchUserDevices = async () => {
    try {
      const { data } = await API.getIglooWorkUserDevices(clientId)
      if (data && data.length > 0) {
        setIsUserConnected(true)
        setInitialValues({
          selectedDevices: data,
          expirationDate: getEarliestKeyExpiry(data) ?? expirationDateInitialValue,
        })
      }
    } catch {
      // not connected or no devices assigned
    }
  }

  const handleCreateUser = async () => {
    setIsCreatingUser(true)
    try {
      await API.createIglooWorkUser(clientId)
      let assignedDevices = []
      try {
        const { data } = await API.getIglooWorkUserDevices(clientId)
        assignedDevices = data || []
      } catch {
        // fall back to empty selection if the fetch fails
      }
      setInitialValues({
        selectedDevices: assignedDevices,
        expirationDate: getEarliestKeyExpiry(assignedDevices) ?? expirationDateInitialValue,
      })
      setIsUserConnected(true)
      displaySnackbar(t('clients-legacy:profileView.integrations.iglooUserCreated'))
    } catch (error) {
      displaySnackbar(
        getBackendErrorMessage(
          error,
          t('clients-legacy:profileView.integrations.iglooUserCreateError'),
        ),
        true,
      )
    } finally {
      setIsCreatingUser(false)
      setIsCreateUserPopupVisible(false)
    }
  }

  const handleUpdateDevices = async (selectedDevices, expirationDate) => {
    const deviceIds = (selectedDevices || []).map((d) => d.id)
    setIsSubmitting(true)
    try {
      setIsConfirmPopupVisible(false)
      const { data } = await API.updateIglooWorkUserDevices(
        clientId,
        deviceIds,
        expirationDate || undefined,
      )
      displaySnackbar(t('clients-legacy:profileView.integrations.iglooDeviceAccessUpdated'))
      const persistedDevices = Array.isArray(data) ? data : selectedDevices || []
      setInitialValues((previous) => ({
        selectedDevices: persistedDevices,
        expirationDate: expirationDate ?? previous.expirationDate,
      }))
      await checkConnectionAndFetchUserDevices()
    } catch (error) {
      displaySnackbar(
        getBackendErrorMessage(
          error,
          t('clients-legacy:profileView.integrations.iglooUpdateDevicesError'),
        ),
        true,
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  useEffect(() => {
    if (isAvailable) {
      setIsLoading(true)
      Promise.all([fetchBuildingDevices(), checkConnectionAndFetchUserDevices()]).finally(() =>
        setIsLoading(false),
      )
    }
  }, [isAvailable])

  if (!isAvailable) return null

  if (isLoadingBuildingConnection) {
    return (
      <div className="information-element">
        <Loader />
      </div>
    )
  }

  if (!isBuildingConnected) {
    return (
      <div className="information-element">
        <Alert
          type="error"
          title={t('clients:keylessAccess.igloo.buildingNotConnected.title')}
          message={t('clients:keylessAccess.igloo.buildingNotConnected.message')}
        />
      </div>
    )
  }

  const connected =
    isUserConnected ||
    (algoPinAvailable && pinData.status !== 'none') ||
    (customPinAvailable && customPinData.status !== 'none')

  const pinBlocks = (
    <>
      {algoPinAvailable && !pinLoading && (
        <IglooPinBlock
          pin={pinData}
          canEdit={canEdit}
          residentName={residentName}
          onIssue={handleIssuePin}
          onRevoke={handleRevokePin}
          onResend={handleResendPinEmail}
          isIssuing={isIssuing}
          isRevoking={isRevoking}
          isResending={isResending}
        />
      )}

      {customPinAvailable && !customPinLoading && (
        <IglooCustomPinBlock
          pin={customPinData}
          canEdit={canEdit}
          residentName={residentName}
          onRevoke={handleRevokeCustomPin}
          onResend={() => resendCustomPinEmail()}
          isRevoking={isRevokingCustomPin}
          isResending={isResendingCustomPin}
        />
      )}
    </>
  )

  return (
    <div className={`information-element ${connected ? '' : 'information-element-column'}`}>
      {connected ? (
        <Formik
          initialValues={formikInitialValues}
          enableReinitialize
          validationSchema={IglooWorkSchema}
          onSubmit={({ selectedDevices, expirationDate, confirmed }) => {
            if (!confirmed) return
            const isExpirationDirty = expirationDate !== formikInitialValues.expirationDate
            handleUpdateDevices(selectedDevices, isExpirationDirty ? expirationDate : undefined)
          }}
        >
          {({ values, errors, touched, setFieldValue, handleSubmit, resetForm }) => {
            const hasError = (field) => errors[field] && touched[field]

            const onChangeExpirationDate = (selectedDate) => {
              const date = new Date(selectedDate)
              setFieldValue('expirationDate', date.getTime() || '')
            }

            const handleConfirm = () => {
              setFieldValue('confirmed', true)
              setIsConfirmPopupVisible(true)
            }

            return (
              <Form>
                <div className="columns">
                  <div className="description-column">
                    <div className="element-name element-margin-top">
                      {t('clients-legacy:profileView.integrations.iglooLabel')}
                    </div>

                    <div className="dropdown-buttons-container">
                      <div className="dropdown-buttons-container-row">
                        <div className="dropdown-container">
                          <div className="dropdown-header">
                            {t('clients-legacy:profileView.integrations.doorLabel')}
                          </div>
                          <Select
                            styles={saltoSpaceDropdownStyles(hasError('selectedDevices'))}
                            className="basic-multi-select"
                            classNamePrefix="select"
                            options={availableDevices}
                            getOptionLabel={(option) => option.name}
                            getOptionValue={(option) => option.id}
                            value={values.selectedDevices}
                            onChange={(selectedOptions) =>
                              setFieldValue('selectedDevices', selectedOptions || [])
                            }
                            placeholder={t(
                              'clients-legacy:profileView.integrations.selectDoorPlaceholder',
                            )}
                            isClearable
                            isMulti
                            closeMenuOnSelect={false}
                          />
                          {hasError('selectedDevices') && (
                            <div className="error-validation">{errors.selectedDevices}</div>
                          )}
                        </div>
                        <div className="dropdown-container">
                          <div className="dropdown-header">
                            {t('clients-legacy:profileView.integrations.expirationDateLabel')}
                          </div>
                          <DatePicker
                            className={`dropdown-button ${
                              hasError('expirationDate') ? 'dropdown-button-error-color' : ''
                            }`}
                            placeholderText={t(
                              'clients-legacy:profileView.integrations.dateFormatPlaceholder',
                            )}
                            selected={values.expirationDate || null}
                            onChange={(selectedDate) => onChangeExpirationDate(selectedDate)}
                            minDate={getCurrentDate()}
                            isClearable
                          />
                          {hasError('expirationDate') && (
                            <div className="error-validation">{errors.expirationDate}</div>
                          )}
                        </div>
                      </div>
                    </div>

                    <button
                      type="button"
                      className={`confirm-button button-primary button ${isSubmitting ? 'is-loading' : ''}`}
                      disabled={isSubmitting}
                      onClick={handleConfirm}
                    >
                      {t('common:actions.confirm')}
                    </button>

                    {pinBlocks}
                  </div>
                </div>

                <ConfirmPopup
                  title={t('common:confirmPopup.caution')}
                  body={
                    values.expirationDate !== formikInitialValues.expirationDate
                      ? t(
                          'clients-legacy:profileView.integrations.iglooUpdateConfirmBodyReissueWarning',
                        )
                      : t('clients-legacy:profileView.integrations.iglooUpdateConfirmBody')
                  }
                  cancelText={t('common:actions.close')}
                  confirmText={t('clients-legacy:profileView.integrations.yesConfirm')}
                  isOpen={isConfirmPopupVisible}
                  cancelCallback={() => {
                    resetForm()
                    setIsConfirmPopupVisible(false)
                  }}
                  confirmCallback={handleSubmit}
                  className="is-caution"
                />
              </Form>
            )
          }}
        </Formik>
      ) : isLoading ? (
        <div className="information-element-row">
          <Loader />
        </div>
      ) : (
        <>
          <div className="information-element-row">
            <div className="element-name">
              {t('clients-legacy:profileView.integrations.iglooLabel')}
            </div>
            <div className="columns">
              <div className="column column-right">
                <button
                  className={`button button-primary ${isCreatingUser ? 'is-loading' : ''}`}
                  disabled={isCreatingUser}
                  onClick={() => setIsCreateUserPopupVisible(true)}
                >
                  {isCreatingUser
                    ? t('clients-legacy:profileView.integrations.creatingEllipsis')
                    : t('clients-legacy:profileView.integrations.createIglooUser')}
                </button>
              </div>
            </div>
          </div>
          {pinBlocks}
        </>
      )}

      <ConfirmPopup
        title={t('common:confirmPopup.caution')}
        body={t('clients-legacy:profileView.integrations.createIglooUserConfirmBody')}
        isOpen={isCreateUserPopupVisible}
        cancelText={t('common:actions.close')}
        confirmText={t('clients-legacy:profileView.integrations.yesCreate')}
        cancelCallback={() => setIsCreateUserPopupVisible(false)}
        confirmCallback={handleCreateUser}
        className="is-caution"
        isLoading={isCreatingUser}
      />
    </div>
  )
}

export default IglooWorkSection
